import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react'
import { useParams } from 'react-router-dom'
import { CompanyTypeSlug } from 'models/companies'
import { OpportunitySubCategorySlug } from 'models/opportunities'
import { featureFlagService } from 'utils/featureFlagService'

export type TabKey =
  | 'fb-companies'
  | 'fb-locations'
  | 'custom-companies'
  | 'custom-locations'

export interface IOppportunitiesContext {
  selectedTag: TabKey
  unmatched: boolean
  noKnownRecords: boolean
  setSelectedTab: (tab: TabKey) => void
}

const initialContext: IOppportunitiesContext = {
  selectedTag: 'fb-companies',
  unmatched: false,
  noKnownRecords: false,
  setSelectedTab: () => {},
}

export const OpportunitiesContext =
  createContext<IOppportunitiesContext>(initialContext)

export const OpportunitiesProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const params = useParams() as { sub_category?: CompanyTypeSlug }
  const companyTypeSlug = params?.sub_category
  const { enableLodging } = featureFlagService()

  const [rawSelectedTab, setRawSelectedTab] = useState<TabKey>('fb-companies')
  const prevSlugRef = useRef(companyTypeSlug)

  const noKnownRecords = useMemo(() => {
    const allSlugs: OpportunitySubCategorySlug[] = [
      ...Object.values(OpportunitySubCategorySlug),
    ]
    if (!enableLodging) {
      if (companyTypeSlug == OpportunitySubCategorySlug.LODGING_HOSPITALITY) {
        return true
      }
    }
    return Boolean(
      companyTypeSlug &&
      !allSlugs.includes(companyTypeSlug as OpportunitySubCategorySlug)
    )
  }, [companyTypeSlug, enableLodging])

  // Reset tab when noKnownRecords changes
  useEffect(() => {
    setRawSelectedTab(noKnownRecords ? 'custom-companies' : 'fb-companies')
  }, [noKnownRecords, companyTypeSlug])

  // Derive effective selected tab synchronously to avoid render gap
  const selectedTag: TabKey = useMemo(() => {
    // Always prevent FB tabs when no known records exist
    if (
      noKnownRecords &&
      (rawSelectedTab === 'fb-companies' || rawSelectedTab === 'fb-locations')
    ) {
      return 'custom-companies'
    }
    // Only snap custom→FB during sub-category navigation, not on user clicks
    if (
      prevSlugRef.current !== companyTypeSlug &&
      !noKnownRecords &&
      (rawSelectedTab === 'custom-companies' ||
        rawSelectedTab === 'custom-locations')
    ) {
      return 'fb-companies'
    }
    return rawSelectedTab
  }, [rawSelectedTab, noKnownRecords, companyTypeSlug])

  useEffect(() => {
    prevSlugRef.current = companyTypeSlug
  }, [companyTypeSlug])

  const unmatched =
    selectedTag === 'custom-companies' || selectedTag === 'custom-locations'

  return (
    <OpportunitiesContext.Provider
      value={{
        unmatched,
        selectedTag,
        noKnownRecords,
        setSelectedTab: setRawSelectedTab,
      }}
    >
      {children}
    </OpportunitiesContext.Provider>
  )
}

export const useOpportunitiesContext = () => {
  const context = useContext(OpportunitiesContext)
  if (!context) {
    throw new Error(
      'useOpportunitiesContext must be used within a OpportunitiesProvider'
    )
  }
  return context
}
